import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getAudit, saveAudit, deleteAudit } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useAwakeMember } from "@/lib/awakeContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Bookmark, Check, Linkedin, Download, ArrowRight } from "lucide-react";

function ScoreRing({ value }) {
  const v = Math.max(0, Math.min(100, value || 0));
  return (
    <div className="flex items-baseline gap-2">
      <div className="font-display text-7xl md:text-8xl tracking-tighter leading-none">{v}</div>
      <div className="text-[var(--muted)] font-mono-mark text-sm">/100</div>
    </div>
  );
}

function CategoryRow({ cat, idx }) {
  // null score = "not assessed" — we didn't have data, so we're being honest about it
  const notAssessed = cat.score === null || cat.score === undefined;
  const score = notAssessed ? null : cat.score;
  return (
    <div
      data-testid={`category-row-${idx}`}
      className={`card-tech rounded-sm p-6 flex flex-col h-full ${notAssessed ? "opacity-90" : ""}`}
    >
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="overline">{String(idx + 1).padStart(2, "0")}</div>
          <h3 className="font-display text-2xl tracking-tight mt-1">{cat.name}</h3>
        </div>
        {notAssessed ? (
          <div
            className="font-mono-mark text-[10px] uppercase tracking-wider px-2 py-1 rounded-sm border border-[var(--line)] text-[var(--muted)] self-start"
            data-testid={`category-not-assessed-${idx}`}
          >
            Not assessed
          </div>
        ) : (
          <div className="font-display text-3xl">
            {score}
            <span className="text-sm text-[var(--muted)] font-mono-mark ml-1">/10</span>
          </div>
        )}
      </div>
      {!notAssessed && (
        <div className="bar-track mt-4">
          <div
            className="bar-fill"
            style={{
              width: `${score * 10}%`,
              background:
                score >= 8 ? "var(--ok)" : score >= 5 ? "var(--ink)" : "var(--accent)",
            }}
          />
        </div>
      )}
      <p className={`mt-4 ${notAssessed ? "text-[var(--muted)] italic" : "text-[var(--ink-2)]"}`}>
        {cat.summary}
      </p>
      {cat.suggestions?.length > 0 && (
        <ul className="mt-4 space-y-2 text-sm">
          {cat.suggestions.map((s, i) => (
            <li key={i} className="flex gap-2">
              <span className="text-[var(--accent)]">→</span>
              <span>{s}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function Results() {
  const { id: rawId } = useParams();
  // Trim whitespace / invisible chars (zero-width spaces from chat-app
  // copy-paste are a common cause of "audit not found"). Without this, a
  // perfectly valid URL pasted via mobile share would 404.
  const id = (rawId || "").trim().replace(/[\u200B-\u200D\uFEFF]/g, "");
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isMember, dashboardUrl } = useAwakeMember();
  const [audit, setAudit] = useState(null);
  const [saved, setSaved] = useState(false);
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    if (!id) {
      setLoadError({ kind: "missing", message: "No audit ID in the URL." });
      return;
    }
    let cancelled = false;
    setLoadError(null);
    // Try cached first
    const cached = sessionStorage.getItem(`audit_${id}`);
    if (cached) {
      try {
        const parsed = JSON.parse(cached);
        if (parsed && parsed.audit_id) {
          setAudit(parsed);
          return;
        }
      } catch {
        /* fall through to fetch */
      }
    }
    getAudit(id)
      .then((data) => {
        if (cancelled) return;
        setAudit(data);
        try {
          sessionStorage.setItem(`audit_${id}`, JSON.stringify(data));
        } catch {
          /* sessionStorage may be full or disabled — non-fatal */
        }
      })
      .catch((e) => {
        if (cancelled) return;
        const status = e?.response?.status;
        if (status === 404) {
          setLoadError({
            kind: "not_found",
            message:
              "We couldn't find this audit. Double-check the link — and if it's correct, it may have been removed.",
          });
        } else if (!e?.response) {
          // Network / CORS / DNS — request never reached the server (or browser refused the reply).
          setLoadError({
            kind: "network",
            message:
              "We couldn't reach the audit service. This is usually a network blip — please refresh in a moment.",
          });
          toast.error("Network error loading audit. Refresh to retry.");
        } else {
          setLoadError({
            kind: "server",
            message: `Something went wrong loading this audit (${status}). Please try again shortly.`,
          });
          toast.error("Couldn't load audit.");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  const handleSave = async () => {
    if (!user) {
      // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
      const redirectUrl = window.location.origin + `/app/audits?save=${id}`;
      window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(
        redirectUrl
      )}`;
      return;
    }
    try {
      await saveAudit(id);
      setSaved(true);
      toast.success("Saved to your workspace.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save.");
    }
  };

  if (!audit) {
    if (loadError) {
      return (
        <div
          data-testid="results-error"
          className="section-pad py-24 md:py-32 max-w-2xl"
        >
          <div className="overline mb-3 text-[var(--accent)]">
            {loadError.kind === "not_found" ? "Audit not found" : "Couldn't load audit"}
          </div>
          <h1 className="font-display text-4xl md:text-5xl tracking-tighter">
            {loadError.kind === "not_found"
              ? "We couldn't find that audit."
              : "We hit a snag loading your audit."}
          </h1>
          <p className="mt-5 text-[var(--ink-2)]">{loadError.message}</p>
          <div className="mt-3 text-xs text-[var(--muted)] font-mono-mark">
            Ref: {id || "—"}
          </div>
          <div className="mt-8 flex flex-wrap gap-3">
            <Button
              data-testid="results-retry-btn"
              onClick={() => window.location.reload()}
              className="rounded-full h-11 px-5"
            >
              Try again
            </Button>
            <Button
              data-testid="results-home-btn"
              variant="outline"
              onClick={() => navigate("/")}
              className="rounded-full h-11 px-5 border-[var(--ink)]"
            >
              Back to home
            </Button>
          </div>
        </div>
      );
    }
    return (
      <div className="section-pad py-32 text-center" data-testid="results-loading">
        <div className="overline">Loading audit…</div>
      </div>
    );
  }

  const tone = audit.tone_of_voice || {};

  return (
    <div data-testid="results-page" className="section-pad py-10 md:py-14">
      {/* Prominent ready banner */}
      <div
        className="no-print mb-10 rounded-sm overflow-hidden border-2"
        style={{ borderColor: "var(--teal)" }}
        data-testid="ready-banner"
      >
        <div className="bg-[var(--teal)] text-white px-5 md:px-7 py-5 md:py-6">
          <div className="flex items-start gap-3 flex-wrap">
            <Check size={22} className="mt-1 shrink-0" />
            <div className="flex-1 min-w-[240px]">
              <div className="overline" style={{ color: "rgba(255,255,255,0.8)" }}>
                Audit complete
              </div>
              <h2 className="font-display text-2xl md:text-3xl tracking-tight mt-1">
                Your audit is ready — scroll down to read it.
              </h2>
            </div>
          </div>
        </div>
        <div className="bg-[var(--paper)] px-5 md:px-7 py-5 grid grid-cols-1 md:grid-cols-12 gap-4 items-center">
          <div className="md:col-span-7 text-sm text-[var(--ink-2)] leading-relaxed">
            <div>
              <span className="font-medium text-[var(--ink)]">How to keep it:</span>{" "}
              Use <span className="font-mono-mark text-xs">Download / Print</span> to save as PDF,
              or bookmark this URL to return to your full report any time.
            </div>
            <div className="text-xs text-[var(--muted)] mt-2">
              We have a copy on file under <span className="font-mono-mark">{audit.email}</span>.
              Emailed delivery is coming soon — for now, save or bookmark.
            </div>
          </div>
          <div className="md:col-span-5 flex flex-wrap md:justify-end gap-2">
            <Button
              onClick={() => window.print()}
              data-testid="download-pdf-btn"
              variant="outline"
              className="rounded-full h-11 md:h-10 px-4 border-[var(--ink)] inline-flex items-center gap-2"
            >
              <Download size={14} /> Download / Print
            </Button>
            {isMember ? (
              <a
                href={dashboardUrl}
                data-testid="results-back-to-dashboard"
                className="btn-red rounded-full h-11 md:h-10 px-4 inline-flex items-center gap-2 text-sm"
              >
                ← Back to your dashboard
              </a>
            ) : (
              <a
                href="https://awake.wearetheweave.co.uk/join"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="results-join-btn"
                className="btn-red rounded-full h-11 md:h-10 px-4 inline-flex items-center gap-2 text-sm"
              >
                Unlock the coach <ArrowRight size={14} />
              </a>
            )}
          </div>
        </div>
      </div>

      {/* HEADER */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8 items-end pb-12 border-b border-[var(--line)]">
        <div className="col-span-12 md:col-span-8">
          <div className="overline">Your founder brand audit · {audit.audit_id}</div>
          <h1
            data-testid="results-title"
            className="font-display text-5xl md:text-7xl tracking-tighter mt-3 max-w-3xl"
          >
            {audit.founder_name || "Founder"} —{" "}
            <span className="text-[var(--accent)]">your brand audit</span>
          </h1>
          <p className="mt-5 text-lg text-[var(--ink-2)] max-w-2xl">{audit.headline_summary}</p>
          {audit.linkedin_url && (
            <a
              href={audit.linkedin_url}
              target="_blank"
              rel="noreferrer"
              className="mt-4 inline-flex items-center gap-2 text-sm text-[var(--ink-2)] hover:text-[var(--accent)]"
            >
              <Linkedin size={14} /> {audit.linkedin_url}
            </a>
          )}
        </div>
        <div className="col-span-12 md:col-span-4 md:text-right">
          <div className="overline">Overall</div>
          <ScoreRing value={audit.overall_score} />
        </div>
      </div>

      {/* CATEGORIES */}
      <section className="py-16">
        <div className="overline mb-3">Scorecard · 11 categories</div>
        <h2 className="font-display text-3xl md:text-4xl tracking-tighter mb-10">
          Where your profile lands.
        </h2>
        <div data-testid="categories-grid" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {(audit.categories || []).map((c, i) => (
            <CategoryRow key={c.name + i} cat={c} idx={i} />
          ))}
        </div>
      </section>

      {/* STRATEGIC OVERVIEW */}
      <section className="py-16 border-t border-[var(--line)]">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-10">
          <div className="col-span-12 md:col-span-4">
            <div className="overline mb-3">Strategic overview</div>
            <h2 className="font-display text-3xl md:text-4xl tracking-tighter">
              What your LinkedIn is currently saying — and what it could.
            </h2>
          </div>
          <div className="col-span-12 md:col-span-8">
            <div
              data-testid="strategic-overview"
              className="text-lg leading-relaxed text-[var(--ink-2)] whitespace-pre-line"
            >
              {audit.strategic_overview}
            </div>
          </div>
        </div>
      </section>

      {/* CONTENT PILLARS */}
      <section className="py-16 border-t border-[var(--line)]">
        <div className="overline mb-3">Content pillars</div>
        <h2 className="font-display text-3xl md:text-4xl tracking-tighter mb-10">
          The themes you should own.
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {(audit.content_pillars || []).map((p, i) => (
            <div key={i} className="card-tech rounded-sm p-6" data-testid={`pillar-${i}`}>
              <div className="overline">Pillar {String(i + 1).padStart(2, "0")}</div>
              <h3 className="font-display text-xl tracking-tight mt-1">{p.name}</h3>
              <p className="text-sm text-[var(--ink-2)] mt-3">{p.description}</p>
              <div className="mt-4 flex flex-wrap gap-2">
                {(p.keywords || []).map((k) => (
                  <Badge
                    key={k}
                    variant="outline"
                    className="rounded-sm border-[var(--line)] font-mono-mark text-[10px]"
                  >
                    {k}
                  </Badge>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* BLOG TITLES */}
      <section className="py-16 border-t border-[var(--line)]">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-10">
          <div className="col-span-12 md:col-span-4">
            <div className="overline mb-3">Editorial calendar</div>
            <h2 className="font-display text-3xl md:text-4xl tracking-tighter">
              10 blog titles you could write next.
            </h2>
            <p className="mt-4 text-[var(--ink-2)] max-w-sm">
              Built from your positioning, tone, and content pillars. Pick three and ship.
            </p>
          </div>
          <ol className="col-span-12 md:col-span-8" data-testid="blog-titles">
            {(audit.blog_titles || []).map((b, i) => (
              <li
                key={i}
                className="grid grid-cols-12 gap-4 py-6 border-t border-[var(--line)] last:border-b"
              >
                <span className="col-span-1 font-mono-mark text-xs text-[var(--muted)]">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <div className="col-span-11">
                  <h3 className="font-display text-2xl tracking-tight">{b.title}</h3>
                  <p className="text-sm text-[var(--ink-2)] mt-2">{b.angle}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* SAMPLE POSTS */}
      <section className="py-16 border-t border-[var(--line)]">
        <div className="overline mb-3">Sample posts</div>
        <h2 className="font-display text-3xl md:text-4xl tracking-tighter mb-10">
          Three LinkedIn posts you could publish this week.
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {(audit.sample_posts || []).map((p, i) => (
            <div key={i} className="card-tech rounded-sm p-6 flex flex-col" data-testid={`post-${i}`}>
              <div className="overline">Post {String(i + 1).padStart(2, "0")}</div>
              <p className="font-display text-xl tracking-tight mt-2">{p.hook}</p>
              <div className="hairline my-4" />
              <p className="text-sm text-[var(--ink-2)] whitespace-pre-line">{p.body}</p>
              <div className="hairline my-4" />
              <p className="text-sm font-medium">{p.cta}</p>
            </div>
          ))}
        </div>
      </section>

      {/* TONE */}
      <section className="py-16 border-t border-[var(--line)]">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-10">
          <div className="col-span-12 md:col-span-4">
            <div className="overline mb-3">Tone of voice</div>
            <h2 className="font-display text-3xl md:text-4xl tracking-tighter">
              How you should sound.
            </h2>
            {tone.primary_tone && (
              <div className="mt-6">
                <div className="overline">Primary tone</div>
                <div className="font-display text-3xl text-[var(--accent)]">{tone.primary_tone}</div>
              </div>
            )}
            {tone.descriptors?.length > 0 && (
              <div className="mt-6 flex flex-wrap gap-2">
                {tone.descriptors.map((d) => (
                  <span
                    key={d}
                    className="border border-[var(--line)] rounded-sm px-3 py-1 text-xs font-mono-mark uppercase tracking-[0.16em]"
                  >
                    {d}
                  </span>
                ))}
              </div>
            )}
          </div>
          <div className="col-span-12 md:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div className="card-tech rounded-sm p-6">
              <div className="overline text-[var(--ok)]">Do</div>
              <ul className="mt-3 space-y-2 text-[var(--ink-2)]">
                {(tone.do || []).map((x, i) => (
                  <li key={i} className="flex gap-2">
                    <span className="text-[var(--ok)]">+</span>
                    {x}
                  </li>
                ))}
              </ul>
            </div>
            <div className="card-tech rounded-sm p-6">
              <div className="overline" style={{ color: "var(--bad)" }}>
                Don't
              </div>
              <ul className="mt-3 space-y-2 text-[var(--ink-2)]">
                {(tone.dont || []).map((x, i) => (
                  <li key={i} className="flex gap-2">
                    <span style={{ color: "var(--bad)" }}>−</span>
                    {x}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA — different content for verified Awake members vs. visitors */}
      <section className="no-print py-20 border-t border-[var(--line)] block-teal -mx-[clamp(20px,6vw,96px)] px-[clamp(20px,6vw,96px)] mt-16">
        {isMember ? (
          <div className="max-w-3xl" data-testid="member-results-cta">
            <div className="overline mb-3" style={{ color: "rgba(251,247,240,0.7)" }}>
              You're already inside the room. Take this back with you.
            </div>
            <h3 className="font-display text-4xl md:text-6xl tracking-tight">
              Your audit is the <span className="squiggle">spine</span>
              <br />
              of your coaching practice.
            </h3>
            <p className="mt-5 text-lg" style={{ color: "rgba(251,247,240,0.85)" }}>
              Bring this audit into your member dashboard. Your strategic overview, content pillars
              and tone profile become the working brief for your Founder Branding Coach — pillars
              get tracked, posts get drafted in your voice, and your audit score gets re-run on a
              cadence that suits you.
            </p>
            <ul className="mt-6 space-y-2 text-base" style={{ color: "rgba(251,247,240,0.9)" }}>
              <li>· Drop these pillars straight into your content calendar</li>
              <li>· Turn the 10 blog titles into a real publishing rhythm</li>
              <li>· Get coach feedback on every draft in your tone</li>
              <li>· Re-run the audit quarterly to track real movement</li>
            </ul>
            <div className="mt-7 flex flex-wrap items-center gap-3">
              <a
                href={dashboardUrl}
                data-testid="member-back-to-dashboard-btn"
                className="btn-red rounded-full h-12 px-8 text-base inline-flex items-center gap-2"
              >
                ← Back to your dashboard
              </a>
              <button
                type="button"
                onClick={() => window.print()}
                data-testid="member-download-btn"
                className="rounded-full h-12 px-8 text-base inline-flex items-center border-2 gap-2"
                style={{
                  borderColor: "rgba(251,247,240,0.6)",
                  color: "var(--cream)",
                }}
              >
                <Download size={16} /> Save a copy
              </button>
            </div>
          </div>
        ) : (
        <div className="max-w-3xl">
          <div className="overline mb-3" style={{ color: "rgba(251,247,240,0.7)" }}>
            Your audit is the start. Membership is where the work happens.
          </div>
          <h3 className="font-display text-4xl md:text-6xl tracking-tight">
            Access your{" "}
            <span className="squiggle">personalised</span>
            <br />
            Founder Branding Coach.
          </h3>
          <p className="mt-5 text-lg" style={{ color: "rgba(251,247,240,0.85)" }}>
            Inside The Weave, your audit becomes the spine of a coached personal-branding
            workspace — mission builder, content pillars, weekly drafts, AI feedback, and a
            community of founders doing the work alongside you.
          </p>
          <ul
            className="mt-6 space-y-2 text-base"
            style={{ color: "rgba(251,247,240,0.9)" }}
          >
            <li>· Build on the strategic overview above with your coach</li>
            <li>· Turn the 10 blog titles into a real publishing calendar</li>
            <li>· Get AI feedback on every post before you hit send</li>
            <li>· Track your audit score over months, not weeks</li>
          </ul>
          <div className="mt-7 flex flex-wrap items-center gap-3">
            <a
              href="https://awake.wearetheweave.co.uk/join"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="join-weave-btn"
              className="btn-red rounded-full h-12 px-8 text-base inline-flex items-center gap-2"
            >
              Join now <ArrowRight size={16} />
            </a>
            <a
              href="https://www.wearetheweave.co.uk/community-choice"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="find-out-more-btn"
              className="rounded-full h-12 px-8 text-base inline-flex items-center border-2"
              style={{
                borderColor: "rgba(251,247,240,0.6)",
                color: "var(--cream)",
              }}
            >
              Find out more
            </a>
          </div>
        </div>
        )}
      </section>

      {/* DELETE MY REPORT — small, calm footer affordance (GDPR) */}
      <DeleteReportFooter
        auditId={audit.audit_id}
        auditEmail={audit.email}
      />
    </div>
  );
}

function DeleteReportFooter({ auditId, auditEmail }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [emailInput, setEmailInput] = useState("");
  const [busy, setBusy] = useState(false);

  const onConfirm = async () => {
    const expected = (auditEmail || "").trim().toLowerCase();
    const entered = emailInput.trim().toLowerCase();
    if (!entered || entered !== expected) {
      toast.error("Email doesn't match the one this audit was generated under.");
      return;
    }
    setBusy(true);
    try {
      await deleteAudit(auditId, { email: entered });
      try {
        sessionStorage.removeItem(`audit_${auditId}`);
      } catch {}
      toast.success("Audit deleted. It will be permanently removed within 30 days.");
      navigate("/");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't delete this audit.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      className="no-print mt-12 pt-8 border-t border-[var(--line)] text-sm text-[var(--muted)]"
      data-testid="delete-report-footer"
    >
      {!open ? (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p>
            Don't want this audit on file?{" "}
            <button
              type="button"
              onClick={() => setOpen(true)}
              className="underline underline-offset-2 hover:text-[var(--accent)]"
              data-testid="delete-report-open-btn"
            >
              Delete my report
            </button>
            . We'll soft-delete immediately and hard-purge after 30 days.
          </p>
        </div>
      ) : (
        <div
          className="card-tech rounded-sm p-5 max-w-xl"
          data-testid="delete-report-confirm-card"
        >
          <div className="overline text-[var(--accent)]">Delete this audit?</div>
          <p className="mt-2 text-[var(--ink-2)]">
            Type the email this audit was generated under to confirm. The audit
            will disappear from your link and any saved workspace; we'll permanently
            purge it within 30 days.
          </p>
          <input
            type="email"
            value={emailInput}
            onChange={(e) => setEmailInput(e.target.value)}
            placeholder={auditEmail || "you@example.com"}
            autoComplete="email"
            data-testid="delete-report-email-input"
            className="mt-4 w-full h-11 rounded-sm border border-[var(--line)] px-3 font-mono-mark text-sm bg-[var(--paper)]"
          />
          <div className="mt-4 flex flex-wrap gap-3">
            <Button
              type="button"
              onClick={onConfirm}
              disabled={busy}
              data-testid="delete-report-confirm-btn"
              className="rounded-full h-11 px-5"
              style={{ backgroundColor: "var(--accent)", color: "white" }}
            >
              {busy ? "Deleting…" : "Delete my report"}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setOpen(false);
                setEmailInput("");
              }}
              data-testid="delete-report-cancel-btn"
              className="rounded-full h-11 px-5 border-[var(--ink)]"
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
    </section>
  );
}
